import { useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	CircularProgress,
	FormControl,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Typography,
	Checkbox,
	FormControlLabel,
	Grid,
	Switch,
} from "@mui/material";
import {
	fetchData,
	FetchDataFailureAction,
	FetchDataSuccessAction,
} from "../../../store/actions";
import { getNonComplianceData, jiraChartResponseProfile } from "../../../Service/Apis";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { RootState } from "../../../store/Store";
import { useDispatch, useSelector } from "react-redux";
import MonthButtonGroup from "../../utilization/MonthsButtonGroup";
import { useCustomToast } from "../../Methods/SnackBar";
import BarChartComponent from "../../charts/BarChartComponent";
import { JiraEmployeeBarTooltip } from "../../utilization/utils";
import { isEmployeeAccessRole } from "../../../utils/accessRole";
import {
	barChartSeriesNames,
	jiraChartBlobDownload,
	resolveJiraExportEmployeeIds,
} from "../chartExport";

export type NonComplianceChartProps = {
	employeeId?: string;
	controlledMonth?: string;
	hideMonthButtons?: boolean;
	tooltipScopedSeriesName?: string;
};

const NonComplianceChart = (props: NonComplianceChartProps = {}) => {
	const {
		employeeId,
		controlledMonth,
		hideMonthButtons,
		tooltipScopedSeriesName,
	} = props;
	const dispatch2: ThunkDispatch<
		RootState,
		null,
		FetchDataSuccessAction | FetchDataFailureAction
	> = useDispatch();
	const [nonComplianceData, setNonComplianceData] = useState<any>([]);
	const [teamName, setTeamName] = useState<string>("");
	const [month, setMonth] = useState<string>("1");
	const [selectedFormat, setSelectedFormat] = useState<string>("excel");
	const showToast = useCustomToast();
	const [loading, setLoading] = useState(false);
	const [downloading, setDownloading] = useState(false);
	const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
	const [selectAll, setSelectAll] = useState(true);

	const nrole = useSelector((state: any) => state.auth.selectedRole);
	const paramsData = useMemo(
		() => ({
			...jiraChartResponseProfile,
			role: nrole ?? "Employee",
			...(employeeId ? { employee_ids: [employeeId] } : {}),
		}),
		[nrole, employeeId]
	);

	const fetchNonComplianceData = async (selectedMonth: string) => {
		try {
			setLoading(true);
			const res = await dispatch2(
				fetchData(`${getNonComplianceData}${selectedMonth}`, paramsData)
			);
			setMonth(selectedMonth);
			setNonComplianceData(res);
			const allEmployeeNames = res?.teams
			? res?.teams?.flatMap((team: any) =>
					team?.issues?.flatMap((issue: any) =>
						issue?.assignees?.map((assignee: any) => assignee.assignee_name)
					) || []
				  ) || []
				: res?.issues?.flatMap((issue: any) =>
						issue?.assignees.map((assignee: any) => assignee.assignee_name)
				  ) || [];
			setSelectedEmployees(allEmployeeNames);
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchNonComplianceData(controlledMonth ?? "1");
	}, [controlledMonth, employeeId, nrole]);

	useEffect(() => {
		if (employeeId && tooltipScopedSeriesName) {
			setSelectedEmployees([tooltipScopedSeriesName]);
			setSelectAll(true);
		}
	}, [employeeId, tooltipScopedSeriesName, nonComplianceData]);

	useEffect(() => {
		if (!employeeId || !nonComplianceData?.teams?.length) return;
		const t = nonComplianceData.teams.find(
			(x: any) => x.team_name === "All"
		);
		if (t) setTeamName("All");
	}, [employeeId, nonComplianceData]);

	const handleMonthChange = (selectedMonth: string) => {
		fetchNonComplianceData(selectedMonth);
	};

	const buttonData = [
		{ label: "1M", onClick: () => handleMonthChange("1") },
		{ label: "3M", onClick: () => handleMonthChange("3") },
		{ label: "6M", onClick: () => handleMonthChange("6") },
		{ label: "1Y", onClick: () => handleMonthChange("12") },
	];

	const computedData = useMemo(() => {
		// Handle data for employee role (second format)
		if (Array.isArray(nonComplianceData?.issues || nonComplianceData)) {
			const sourceData = nonComplianceData?.issues || nonComplianceData;
			const data = sourceData.flatMap((issue: any) =>
				issue.assignees.length > 0
					? issue.assignees.map((assignee: any) => ({
							assignee_name: assignee?.assignee_name,
							week: issue?.week_end,
							issue_count: assignee?.issue_count,
							nc_issues: Array.isArray(assignee?.issues)
								? assignee.issues.map((x: any) => ({
										issue_key: x.issue_key,
										title: x.title,
								  }))
								: [],
					  }))
					: [
							{
								week: issue?.week_end,
								assignee_name: "No Assignees",
								issue_count: 0,
								nc_issues: [] as any[],
							},
					  ]
			);

			const groupedData = data.reduce((acc: any, curr: any) => {
				let weekData = acc.find((item: any) => item.week === curr.week);
				if (!weekData) {
					weekData = { week: curr.week, __jiraTooltip: {} };
					acc.push(weekData);
				}
				if (!weekData.__jiraTooltip) weekData.__jiraTooltip = {};
				weekData[curr.assignee_name] = curr.issue_count;
				weekData.__jiraTooltip[curr.assignee_name] = {
					nc_issues: curr.nc_issues || [],
				};
				return acc;
			}, []);

			return groupedData;
		}

		// Handle data for other roles (first format with teams)
		if (!nonComplianceData || !nonComplianceData.teams) {
			return [];
		}

		const selectedTeam =
			nonComplianceData.teams.find(
				(team: any) => team.team_name === teamName
			) || nonComplianceData.teams[0];

		setTeamName(selectedTeam?.team_name);

		if (selectedTeam) {
			const data = selectedTeam.issues.flatMap((issue: any) =>
				issue.assignees.length > 0
					? issue.assignees.map((assignee: any) => ({
							assignee_name: assignee?.assignee_name,
							week: issue?.week_end,
							issue_count: assignee?.issue_count,
							nc_issues: Array.isArray(assignee?.issues)
								? assignee.issues.map((x: any) => ({
										issue_key: x.issue_key,
										title: x.title,
								  }))
								: [],
					  }))
					: [
							{
								week: issue?.week_end,
								assignee_name: "No Assignees",
								issue_count: 0,
								nc_issues: [] as any[],
							},
					  ]
			);

			const groupedData = data.reduce((acc: any, curr: any) => {
				let weekData = acc.find((item: any) => item.week === curr.week);
				if (!weekData) {
					weekData = { week: curr.week, __jiraTooltip: {} };
					acc.push(weekData);
				}
				if (!weekData.__jiraTooltip) weekData.__jiraTooltip = {};
				weekData[curr.assignee_name] = curr.issue_count;
				weekData.__jiraTooltip[curr.assignee_name] = {
					nc_issues: curr.nc_issues || [],
				};
				return acc;
			}, []);

			return groupedData;
		}
		return [];
	}, [nonComplianceData, teamName]);

	const chartEmployeeNames = useMemo(
		() => barChartSeriesNames(computedData),
		[computedData]
	);

	const onDownload = async () => {
		try {
			setDownloading(true);
			const isEmployeeRole =
				(nrole ?? "Employee").toLowerCase() === "employee";

			let selectedTeam: any = null;
			if (!isEmployeeRole) {
				if (!teamName) {
					throw new Error("Please select a team");
				}
				selectedTeam = nonComplianceData.teams?.find(
					(team: any) => team.team_name === teamName
				);
				if (!selectedTeam) {
					throw new Error("Selected team not found");
				}
			}

			let fallbackRows: any[] = [];
			if (isEmployeeRole) {
				const issues = nonComplianceData?.issues || nonComplianceData || [];
				fallbackRows = Array.isArray(issues)
					? issues.flatMap((issue: any) => issue.assignees || [])
					: [];
		} else if (selectedTeam?.issues?.length > 0) {
			fallbackRows = selectedTeam.issues.flatMap((issue: any) => issue.assignees || []);
		}
		const employeeIds = resolveJiraExportEmployeeIds({
			selectedEmployees,
			chartEmployeeNames,
			teamEmployees: selectedTeam?.employees,
			rowsForIdFallback: fallbackRows,
		});

		const baseParams: Record<string, unknown> = {
			...paramsData,
			download: selectedFormat,
		};
			if (employeeId) {
				baseParams.employee_ids = [employeeId];
			} else if (employeeIds) {
				baseParams.employee_ids = employeeIds;
			}
			if (!isEmployeeRole && selectedTeam) {
				baseParams.team_id = selectedTeam.team_id;
			}

			const ext =
				selectedFormat === "excel" ? "xlsx" : selectedFormat;
			const filename = !isEmployeeRole
				? `non-compliance-${teamName.replace(/\s+/g, "-")}.${ext}`
				: `non-compliance.${ext}`;

			await jiraChartBlobDownload(
				`${getNonComplianceData}${month}`,
				baseParams,
				filename,
				showToast
			);
		} catch (error: any) {
			showToast(error?.message ?? String(error), "error", true);
		} finally {
			setDownloading(false);
		}
	};

	// Helper function to get all employee names
	const getAllEmployeeNames = () => {
		return nonComplianceData?.teams
			? nonComplianceData?.teams?.flatMap((issue: any) =>
					issue.employees.map((assignee: any) => assignee.employee_name)
			  ) || []
			: nonComplianceData?.issues?.flatMap((issue: any) =>
					issue.assignees.map((assignee: any) => assignee.assignee_name)
			  ) || [];
	};

	// Update handleEmployeeSelection to manage selectAll state
	const handleEmployeeSelection = (employeeName: string) => {
		const newSelectedEmployees = selectedEmployees.includes(employeeName)
			? selectedEmployees.filter((name) => name !== employeeName)
			: [...selectedEmployees, employeeName];

		setSelectedEmployees(newSelectedEmployees);

		const uniqueEmployees = barChartSeriesNames(computedData);

		if (newSelectedEmployees.length === uniqueEmployees.length) {
			setSelectAll(true);
		}
		if (newSelectedEmployees.length === 0) {
			setSelectAll(false);
		}
	};

	// Replace handleSwitchChange with simpler logic
	const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const checked = event.target.checked;
		setSelectAll(checked);

		if (checked) {
			// Select all employees
			setSelectedEmployees(getAllEmployeeNames());
		} else {
			// Deselect all employees
			setSelectedEmployees([]);
		}
	};

	return (
		<>
			<Paper sx={{ m: 2, p: 4 }}>
				<Box display="flex" justifyContent="space-between" alignItems="center">
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						flexDirection="column"
						gap={2}
					>
						<Typography variant="h4">Non-Compliance Chart</Typography>
						{!hideMonthButtons && (
							<MonthButtonGroup buttonData={buttonData} />
						)}
					</Box>
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						flexDirection="column"
						gap={2}
					>
						{nonComplianceData?.teams && !employeeId && (
							<FormControl
								size="small"
								sx={{ minWidth: 220, width: "100%", maxWidth: 320 }}
							>
								<InputLabel id="noncompliance-team-select-label">
									Select Team
								</InputLabel>
								<Select
									labelId="noncompliance-team-select-label"
									id="noncompliance-team-select"
									value={teamName}
									label="Select Team"
									onChange={(e) => setTeamName(e.target.value)}
								>
									{nonComplianceData?.teams?.map((team: any) => (
										<MenuItem key={team.team_id} value={team.team_name}>
											{team.team_name}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						)}

						<Box
							display="flex"
							justifyContent="center"
							gap={2}
							alignItems="center"
							flexWrap="wrap"
						>
							<FormControl size="small" sx={{ minWidth: 120 }}>
								<InputLabel id="noncompliance-export-format-label">
									Format
								</InputLabel>
								<Select
									labelId="noncompliance-export-format-label"
									label="Format"
									value={selectedFormat}
									onChange={(e) =>
										setSelectedFormat(e.target.value)
									}
								>
									<MenuItem value="excel">
										Excel (.xlsx)
									</MenuItem>
									<MenuItem value="csv">CSV</MenuItem>
									<MenuItem value="json">JSON</MenuItem>
								</Select>
							</FormControl>
							{downloading ? (
								<Box
									display="flex"
									justifyContent="center"
									alignItems="center"
									gap={2}
								>
									<CircularProgress size="1rem" />
									<Typography>Downloading...</Typography>
								</Box>
							) : (
								<Button
									variant="outlined"
									size="small"
									onClick={onDownload}
								>
									Export
								</Button>
							)}
						</Box>
					</Box>
				</Box>
				{!employeeId && !isEmployeeAccessRole(nrole) && (
					<Box
						display="flex"
						justifyContent="flex-end"
						alignItems="center"
						gap={2}
						m={2}
						width="100%"
					>
						<Typography>Select All Employees</Typography>
						<Switch
							checked={selectAll}
							onChange={handleSwitchChange}
							color="primary"
						/>
					</Box>
				)}
				{loading ? (
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						width="100%"
						height={200}
					>
						<CircularProgress />
					</Box>
				) : (
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						flexDirection="column"
					>
						<BarChartComponent
							data={computedData || []}
							YAxisLabel="Issue Count"
							selectedEmployees={selectedEmployees}
							onEmployeeSelection={handleEmployeeSelection}
							tooltipContent={
								<JiraEmployeeBarTooltip
									scopedSeriesName={tooltipScopedSeriesName}
								/>
							}
						/>
					</Box>
				)}
			</Paper>
		</>
	);
};

export default NonComplianceChart;
